import {
  Component,
  computed,
  inject,
  Signal,
  signal,
  ViewChild,
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
// PIPES
import { SquaresPipe } from '../../pipes/squares.pipe';
import { FlagsPipe } from '../../pipes/flags.pipe';
// INTERFACES
import { IDid } from '../../models/dids.model';
import { IPackagePrice } from '../../models/package-price.model';
// COMPONENTS
import { BtnBackComponent } from '../../core/shared/buttons/btn-back/btn-back.component';
import { SpinnerSmallComponent } from '../../core/shared/loading/spinner-small/spinner-small.component';
import { CouponCodeComponent } from '../../core/shared/coupon-code/coupon-code.component';
import { BillingFormComponent } from '../../core/shared/payment/billing-form/billing-form.component';
import { PaymentFormComponent } from '../../core/shared/payment/payment-form/payment-form.component';
// SERVICES
import { SubmitOrderService } from '../../services/submit-order/submit-order.service';
import { CouponCodeService } from '../../services/coupon-code/coupon-code.service';
import { PackagePriceService } from '../../services/package-price/package-price.service';
import { GetDIDsService } from '../../services/getDIDs/get-dids.service';
import {
  AuthService,
  LocalStorageList,
  PersistenceService,
} from 'lib-my-account';
import { LoadingService, PopUpService } from 'lib-shared';
import { GoogleTagManagerService } from '../../services/google-tag-manager/google-tag-manager.service';
import { ConversionApiService } from '../../services/conversion-api/conversion-api.service';
import { CommonModule } from '@angular/common';
import { MonthSelectionComponent } from '../../core/shared/payment/month-selection/month-selection.component';
import { IDidCountries, IPackageCountries } from '../../models/countries.model';
import { filter, take } from 'rxjs';

@Component({
  selector: 'app-payment-page',
  standalone: true,
  imports: [
    CommonModule,
    BillingFormComponent,
    BtnBackComponent,
    CouponCodeComponent,
    FlagsPipe,
    PaymentFormComponent,
    RouterModule,
    SquaresPipe,
    SpinnerSmallComponent,
    TranslocoModule,
    MonthSelectionComponent,
  ],
  templateUrl: './payment-page.component.html',
  styleUrls: ['./payment-page.component.scss'],
})
export class PaymentPageComponent {
  authService = inject(AuthService);
  couponCodeService = inject(CouponCodeService);
  getDIDsService = inject(GetDIDsService);
  gtmService = inject(GoogleTagManagerService);
  loadingService = inject(LoadingService);
  popUpService = inject(PopUpService);
  packagePriceService = inject(PackagePriceService);
  submitOrderService = inject(SubmitOrderService);
  translateService = inject(TranslocoService);
  conversionApiService = inject(ConversionApiService);
  persistenceService = inject(PersistenceService);

  @ViewChild('paymentForm') paymentForm!: PaymentFormComponent;
  @ViewChild('billingForm') billingForm!: BillingFormComponent;

  paymentId: number = 0;
  disablePromoCode: boolean = false;
  couponCode: string = '';
  currentLang: string = '';
  couponPrice: number = 0;
  totalPrice: Signal<number> = signal(0);

  didCountryId: number;
  packageCountry: IPackageCountries | null = null;
  didId: number = 0;

  packageData: IPackagePrice | null = null;
  packagePrice: number = 0;
  didData: IDid | null = null;
  orderData: any = null;

  selectedDuration: number = 1;
  renewalPackageID: number = 0;

  paymentCardForm = this.submitOrderService.submitForm.get(
    'paymentForm'
  ) as FormGroup;
  billingAddressForm = this.submitOrderService.submitForm.get(
    'billingForm'
  ) as FormGroup;
  couponForm = this.submitOrderService.submitForm.get(
    'couponForm'
  ) as FormGroup;
  countriesForm = this.submitOrderService.submitForm.get(
    'countriesForm'
  ) as FormGroup;

  constructor(private router: Router, private route: ActivatedRoute) {}

  ngOnInit(): void {
    this.loadingService.setLoading(true);
    this.submitOrderService
      .getOrderData()
      .pipe(
        filter((orderData) => !!orderData),
        take(1)
      )
      .subscribe((orderData) => {
        this.orderData = orderData;
        if (orderData && orderData.didCountry && orderData.packageCountry) {
          this.packageCountry = orderData.packageCountry;
          this.didCountryId = orderData.didCountry.countryID;
          this.didId = orderData.didData ? orderData.didData.didid : 0;
        } else {
          console.error(
            'Order data not found, redirecting or fetching is needed.'
          );
          this.popUpService.openPopUp('server-error');
          this.router.navigate(['']);
        }
        this.getSelectedDID();

        this.getCurrentLang();
        // this.loadingService.setLoading(false);
      });
      this.loadingService.setLoading(false);
  }

  getCurrentLang() {
    // Subscribe to language changes
    this.translateService.langChanges$.subscribe((lang: string) => {
      this.currentLang = lang; // Update currentLang when language changes
    });

    // Initialize currentLang with the current active language
    this.currentLang = this.translateService.getActiveLang();
  }

  onSelectedSavedCard(cardId: number) {
    this.paymentId = cardId;
  }

  onCouponCodeSubmit(event: string) {
    this.couponCode = event;
    if (this.couponCode !== '') {
      this.validateCouponCode();
    }
  }

  async submitOrder() {
    this.loadingService.setLoading(true);
    this.paymentForm.onSubmitPayment();
    this.billingForm.onSubmitBilling();
    const fullPhone = this.orderData.dialCode + this.orderData.phone;

    if (this.paymentCardForm.valid && this.billingAddressForm.valid) {
      const jsonData = {
        PlatformID: 17,
        CreditCardNumber: this.paymentCardForm.get('cardNumber')?.value,
        CardHolder: this.paymentCardForm.get('cardHolder')?.value,
        SecurityCode: this.paymentCardForm.get('cardCVV')?.value,
        ExpirationMonth: this.paymentCardForm.get('cardExpirationMM')?.value,
        ExpirationYear: this.paymentCardForm.get('cardExpirationYY')?.value,
        BillCountry: this.billingAddressForm.get('country')?.value,
        State: this.billingAddressForm.get('state')?.value,
        City: this.billingAddressForm.get('city')?.value,
        Zip: this.billingAddressForm.get('zipCode')?.value,
        Address: this.billingAddressForm.get('address')?.value,
        Phone: fullPhone,
        Email: this.orderData.email,
        PackageID: this.packageCountry?.packageId,
        Password: this.orderData.password,
        FirstName: this.orderData.firstName,
        LastName: this.orderData.lastName,
        LanguageISO: this.currentLang,
        IsSaveCard: 1,
        IsPrimary: 1,
        PromoCode: this.couponForm.get('couponCode')?.value,
        PromoCodeParams: {
          PackageID: this.packageCountry?.packageId,
          DIDID: this.didData?.didid.toString(),
        },
        DIDID: this.didData?.didid.toString(),
        inquiryTicket: this.orderData.inquiryTicket,
        RenewalPackageID: this.renewalPackageID,
      };

      try {
        const res = await this.submitOrderService.submitOrder(jsonData);

        if (res.response_code === 200) {
          if (this.didData) {
            this.persistenceService.removeData(LocalStorageList.orderData);

            this.gtmService.purchase(
              this.didData,
              res.confirmationId,
              this.packageCountry?.packageId,
              this.packageCountry?.countryName,
              this.totalPrice(),
              this.couponCode,
              this.couponPrice
            );

            this.conversionApiService.trackEvent(
              'Purchase',
              this.orderData.email,
              fullPhone,
              this.didData.price
            );
          }

          this.router.navigate(['/order']);
        } else if (res.response_code === 999) {
          this.popUpService.openPopUp('server-error');
        } else {
          this.popUpService.openPopUp(res.response_message);
        }
      } catch (error) {
        console.error(error);

        if (
          error.error.response_code === 530 ||
          error.error.response_code === 531
        ) {
          this.popUpService.openPopUp('recaptcha');
          this.loadingService.setLoading(false);
        } else {
          this.popUpService.openPopUp('server-error');
        }
      } finally {
        this.loadingService.setLoading(false);
      }
    }
  }

  async validateCouponCode() {
    const jsonData = {
      PackageID: this.packageCountry?.packageId,
      DIDID: this.didData?.didid,
      PromoCode: this.couponForm.get('couponCode')?.value,
      PromoCodeParams: {
        PackageID: this.packageCountry?.packageId,
        DIDID: this.didData?.didid,
      },
    };

    try {
      const res = await this.couponCodeService.validateCouponCode(jsonData);

      if (res.response_code === 200) {
        if (res.discount === 0) {
          this.popUpService.openPopUp('code-not-valid');
        } else {
          this.totalPrice = computed(() => {
            return this.packagePrice - res.discount;
          });
        }

        this.couponPrice = res.discount;
      }
    } catch (error) {
      console.error('Coupon validation Error: ', error);
    }
  }

  async getPackagePrice() {
    try {
      const jsonData = {
        packageId: Number(this.packageCountry?.packageId),
        countryId: this.didCountryId,
        didId: this.didId,
        price: 0,
      };

      const res = await this.packagePriceService.getPackagePrice<IPackagePrice>(
        jsonData
      );

      if (res.isSuccessful && res.result) {
        this.packageData = res.result;
        this.packagePrice = res.result.price;

        setTimeout(() => {
          this.totalPrice = computed(() => {
            return this.packagePrice;
          });
        }, 1000);

        if (this.didData) {
          this.gtmService.addPaymentInfo(
            this.didData,
            this.packagePrice,
            this.packageCountry?.packageId,
            this.packageCountry?.countryName,
            this.couponCode,
            this.couponPrice
          );
          this.conversionApiService.trackEvent(
            'AddPaymentInfo',
            this.orderData.email,
            this.orderData.phone
          );
        }
      }
    } catch (error) {
      console.error(error);
    }
  }

  async getSelectedDID() {
    const res = await this.getDIDsService.getSelectedDID<IDid>(this.didId);

    if (res.isSuccessful) {
      this.didData = res.result;
      this.getPackagePrice();
    }
  }

  onRemoveCouponCode() {
    this.couponCode = '';
    this.couponPrice = 0;

    this.totalPrice = computed(() => {
      return this.packagePrice;
    });
  }

  onDurationSelected(event: { months: number; renewalPackageID?: number }) {
    this.selectedDuration = event.months;
    this.renewalPackageID = event.renewalPackageID || 0;
    this.disablePromoCode = event.renewalPackageID > 0 ? true : false;
    // Buscar la opción seleccionada para obtener el precio correcto
    const selectedOption = this.packageData.discounts.find(
      (d: any) => d.intervalMonths === event.months
    );

    this.packagePrice =
      event.renewalPackageID > 0
        ? selectedOption.total
        : this.packageData.price;

    this.totalPrice = computed(() => {
      return this.packagePrice - this.couponPrice;
    });
  }
}
